import {
  Chalet,
  Booking,
  ExtraOption,
  DayPrices,
  Review,
  UnavailablePeriod,
  CustomPricePeriod,
  BestReview,
  BlogPost,
  BlogListResponse,
} from "../types";
import { GoogleGenAI } from "@google/genai";
import { API_BASE } from "./apiBase";

/** ----------------------------
 * Helpers: normalize + fetch
 * ---------------------------- */

type Json = any;

const toNumber = (v: any, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const toString = (v: any, fallback = "") => {
  if (v === null || v === undefined) return fallback;
  return String(v);
};

/** parse JSON string if needed; otherwise return original */
const tryParseJson = (val: any) => {
  if (typeof val !== "string") return val;
  const s = val.trim();
  if (!s) return val;
  if (
    (s.startsWith("[") && s.endsWith("]")) ||
    (s.startsWith("{") && s.endsWith("}"))
  ) {
    try {
      return JSON.parse(s);
    } catch {
      return val;
    }
  }
  return val;
};

/** Always return an array */
const asArray = <T,>(val: any): T[] => {
  const v = tryParseJson(val);
  if (Array.isArray(v)) return v as T[];
  return [];
};

/** Always return an object (plain associative object) */
const asObject = <T extends Record<string, any>>(val: any): T => {
  const v = tryParseJson(val);
  if (v && typeof v === "object" && !Array.isArray(v)) return v as T;
  return {} as T;
};

/** Extract rows from common response shapes */
const unwrapRows = (data: any): any[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.data)) return data.data;
    // if single object returned
    return [data];
  }
  return [];
};

/** Extract single row (supports array, {data:[...]}, or object) */
const unwrapSingle = (data: any): any | undefined => {
  if (Array.isArray(data)) return data[0];
  if (data && typeof data === "object") {
    if (Array.isArray(data.data)) return data.data[0];
    return data;
  }
  return undefined;
};

const fetchJson = async <T = Json>(
  url: string,
  init: RequestInit = {}
): Promise<T> => {
  const res = await fetch(url, {
    credentials: "include", // 🔐 send PHP session cookie
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  });

  if (!res.ok) {
    // Try to read body for easier debugging (PHP may return HTML)
    const text = await res.text().catch(() => "");
    throw new Error(
      `HTTP ${res.status} for ${url}\n${text.slice(0, 200)}`
    );
  }

  return (await res.json()) as T;
};


/** ----------------------------
 * Mapping: DB -> App Types
 * ---------------------------- */

const mapApartmentToChalet = (j: any): Chalet => {
  // Normalize potentially-problematic fields FIRST
  const images = asArray<string>(j.images).map(String);
  const amenities = asArray<string>(j.amenities).map(String);
  const reviewsRaw = asArray<any>(j.reviews);
  const unavailableRaw = asArray<any>(j.unavailablePeriods);
  const customPriceRaw = asArray<any>(j.customPricePeriods);

  const dp = asObject<DayPrices>(j.dayPrices);
  const dayPrices = Object.keys(dp).length ? dp : undefined;

  return {
    id: toString(j.id),
    title: toString(j.title ?? j.name),
    description: toString(j.description ?? j.description_large),
    location: toString(j.location),
    category: toString(j.category),
    province: toString(j.province, "Onbekend"),

    pricePerNight: toNumber(j.pricePerNight ?? j.base_price ?? j.baseprice, 0),
    cleaningFee: toNumber(j.cleaning_cost ?? j.cleaningFee ?? j.cleaningCost, 0),

    images: images.length ? images : [],

    maxGuests: toNumber(j.maxGuests ?? j.max_guests, 0),
	minNights: Math.max(1, toNumber(j.minNights ?? j.min_nights, 1)),
	minStayPeriods: (() => {
	  const v = j.minStayPeriods;
	  if (Array.isArray(v)) return v;
	  if (typeof v === "string") {
		try {
		  const parsed = JSON.parse(v);
		  return Array.isArray(parsed) ? parsed : [];
		} catch {
		  return [];
		}
	  }
	  return [];
	})(),

    amenities: amenities.length ? amenities : [],

    reviews: reviewsRaw.map((r: any): Review => ({
      id: toString(r.id),
      user: toString(r.user),
      rating: toNumber(r.rating, 0),
      comment: toString(r.comment),
      date: toString(r.date),
    })),

    rating: toNumber(j.rating, 0),
	
	reservationCost: toNumber(j.reservationCost ?? j.reservation_cost ?? 0, 0),
	touristTax: toNumber(j.touristTax ?? j.tourist_tax ?? 0, 0),

    smoobuIcalUrl: toString(j.smoobuIcalUrl ?? j.smoobu_ical_url ?? ""),
    unavailablePeriods: unavailableRaw.map((p: any): UnavailablePeriod => ({
      startDate: toString(p.startDate ?? p.start_date),
      endDate: toString(p.endDate ?? p.end_date),
    })),

    customPricePeriods: customPriceRaw.map((p: any): CustomPricePeriod => ({
      startDate: toString(p.startDate ?? p.start_date),
      endDate: toString(p.endDate ?? p.end_date),
      price: toNumber(p.price, 0),
    })),

    dayPrices,
    isActive: j.is_active !== undefined ? toNumber(j.is_active, 1) : 1,
  };
};

const VALID_STATUSES = [
  "pending",
  "pending_payment",
  "confirmed",
  "cancelled",
  "payment_failed",
  "payment_expired",
] as const;

type BookingStatus = typeof VALID_STATUSES[number];


const mapDbBookingToBooking = (j: any): Booking => ({
  id: toString(j.id),
  chaletId: toString(j.apartment_id ?? j.chaletId ?? j.chalet_id),
  guests: toNumber((j as any).guests ?? (j as any).guestCount ?? (j as any).guest_count, 1),
  guestName: toString(j.customer_name ?? j.guestName),
  guestAddress: toString(j.customer_address ?? j.guestAddress),
  guestCity: toString(j.customer_city ?? j.guestCity),
  guestEmail: toString(j.email ?? j.guestEmail),
  guestPhone: toString(j.customer_phone ?? j.guestPhone),
  guestZip: toString(j.customer_zip ?? j.guestZip),
  startDate: toString(j.start_date ?? j.startDate),
  endDate: toString(j.end_date ?? j.endDate),
  totalPrice: toNumber(j.total_price ?? j.totalPrice, 0),
  extras: asArray<string>(j.extras).map(String),
  createdAt: toString(j.created_at ?? j.createdAt),
  extraBeddengoed: Number(j.extra_beddengoed ?? j.extraBeddengoed ?? 0) === 1,
  status: VALID_STATUSES.includes(j.status)
    ? (j.status as BookingStatus)
    : "pending",
});

/** ----------------------------
 * CHALETS (APARTMENTS) API
 * ---------------------------- */

export const getChalets = async (showAll = false): Promise<Chalet[]> => {
  try {
    const url = showAll
      ? `${API_BASE}/apartments/list.php?showAll=1`
      : `${API_BASE}/apartments/list.php`;
    const data = await fetchJson(url);
    const rows = unwrapRows(data);
    return rows.map(mapApartmentToChalet);
  } catch (error) {
    console.error("Error fetching chalets:", error);
    return [];
  }
};

export const getChaletById = async (id: string): Promise<Chalet | undefined> => {
  try {
    const data = await fetchJson(`${API_BASE}/apartments/list.php?id=${encodeURIComponent(id)}`);
    const row = unwrapSingle(data);
    return row ? mapApartmentToChalet(row) : undefined;
  } catch (error) {
    console.error(`Error fetching chalet ${id}:`, error);
    return undefined;
  }
};

export const addChalet = async (chalet: Chalet): Promise<boolean> => {
  try {
    const data = await fetchJson<{ success?: boolean }>(`${API_BASE}/apartments/create.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(chalet),
    });
    return !!data?.success;
  } catch (error) {
    console.error("Error adding chalet:", error);
    return false;
  }
};

export const updateChalet = async (updatedChalet: Chalet): Promise<boolean> => {
  try {
    const data = await fetchJson<{ success?: boolean }>(`${API_BASE}/apartments/update.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updatedChalet),
    });
    return !!data?.success;
  } catch (error) {
    console.error("Error updating chalet:", error);
    return false;
  }
};

export const deleteChalet = async (id: string): Promise<boolean> => {
  try {
    const data = await fetchJson<{ success?: boolean }>(`${API_BASE}/apartments/delete.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    });
    return !!data?.success;
  } catch (error) {
    console.error("Error deleting chalet:", error);
    return false;
  }
};

export const reorderChalets = async (
  orderedIds: Array<string | number>
): Promise<{ success: boolean; count?: number; error?: string }> => {
  try {
    const res = await fetchJson(`${API_BASE}/apartments/reorder.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ orderedIds }),
    });
    return res ?? { success: false, error: "No response" };
  } catch (error: any) {
    return { success: false, error: error?.message ?? String(error) };
  }
};

export const saveReviews = async (apartmentId: number, reviews: Review[]) => {
  return fetchJson(`${API_BASE}/reviews/save_reviews.php`, {
    method: "POST",
    body: JSON.stringify({ apartmentId, reviews }),
  });
};

/** ----------------------------
 * BOOKINGS API
 * ---------------------------- */

export const getAllBookings = async (): Promise<Booking[]> => {
  try {
    const data = await fetchJson(`${API_BASE}/bookings/list.php`);
    const rows = unwrapRows(data);
    return rows.map(mapDbBookingToBooking);
  } catch (error) {
    console.error("Error fetching bookings:", error);
    return [];
  }
};

export const getBookingsForChalet = async (chaletId: string): Promise<Booking[]> => {
  try {
    const res = await fetch(
      `${API_BASE}/bookings/availability.php?apartment_id=${encodeURIComponent(chaletId)}`
    );

    if (!res.ok) return [];

    const json = await res.json();
    if (!Array.isArray(json)) return []; // important: don't assume shape

    // reuse your existing mapping (it already handles start_date/end_date)
    return json.map(mapDbBookingToBooking);
  } catch {
    return [];
  }
};



export const createBooking = async (
  booking: Booking,
  opts?: { company?: string }
): Promise<{ success: boolean; booking_id?: number; error?: string }> => {
  try {
    const payload = opts?.company ? { ...booking, company: opts.company } : booking;

    const data = await fetchJson<{ success?: boolean; booking_id?: number; error?: string }>(
      `${API_BASE}/bookings/create.php`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );

    return {
      success: !!data?.success,
      booking_id: data?.booking_id,
      error: data?.error,
    };
  } catch (error: any) {
    console.error("Error creating booking:", error);
    return { success: false, error: error?.message ?? "Network/API error" };
  }
};

export const updateBookingStatus = async (
  bookingId: string,
  status: Booking["status"]
): Promise<boolean> => {
  try {
    const data = await fetchJson<{ success?: boolean; error?: string }>(
      `${API_BASE}/bookings/update.php`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: bookingId,
          status,
        }),
      }
    );

    return !!data?.success;
  } catch (error) {
    console.error("Error updating booking status:", error);
    return false;
  }
};

export const sendBookingConfirmation = async (id: string): Promise<boolean> => {
  try {
    const data = await fetchJson(`${API_BASE}/bookings/send_confirmation.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ id }),
    });
    return !!data?.success;
  } catch (e) {
    console.error("sendBookingConfirmation failed:", e);
    return false;
  }
};

export const getBookingNote = async (bookingId: string): Promise<string> => {
  try {
    const data = await fetchJson<{ note?: string }>(
      `${API_BASE}/bookings/note.php?id=${encodeURIComponent(bookingId)}`,
      { credentials: "include" }
    );
    return data?.note ?? "";
  } catch (e) {
    console.error("getBookingNote failed:", e);
    return "";
  }
};

export const saveBookingNote = async (bookingId: string, note: string): Promise<boolean> => {
  try {
    const data = await fetchJson<{ success?: boolean }>(
      `${API_BASE}/bookings/note.php`,
      {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({ id: bookingId, note }),
      }
    );
    return !!data?.success;
  } catch (e) {
    console.error("saveBookingNote failed:", e);
    return false;
  }
};

export const getExtraBeddengoed = async (bookingId: string): Promise<boolean> => {
  try {
    const data = await fetchJson<{ extra_beddengoed?: boolean }>(
      `${API_BASE}/bookings/extra_beddengoed.php?id=${encodeURIComponent(bookingId)}`,
      { credentials: "include" }
    );
    return !!data?.extra_beddengoed;
  } catch (e) {
    console.error("getExtraBeddengoed failed:", e);
    return false;
  }
};

export const saveExtraBeddengoed = async (bookingId: string, value: boolean): Promise<boolean> => {
  try {
    const data = await fetchJson<{ success?: boolean }>(
      `${API_BASE}/bookings/extra_beddengoed.php`,
      {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({ id: bookingId, value }),
      }
    );
    return !!data?.success;
  } catch (e) {
    console.error("saveExtraBeddengoed failed:", e);
    return false;
  }
};

export const resendBookingConfirmation = async (id: string): Promise<boolean> => {
  try {
    const data = await fetchJson(`${API_BASE}/bookings/resend_confirmation.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ id }),
    });
    return !!data?.success;
  } catch (e) {
    console.error("resendBookingConfirmation failed:", e);
    return false;
  }
};

// --- COUPONS API ---
export const getCoupons = async (): Promise<Coupon[]> => {
  try {
    const data = await fetchJson(`${API_BASE}/coupons/list.php`, {
      credentials: "include",
    });

    const rows = unwrapRows(data) ?? [];

    return rows.map((r: any) => ({
      id: String(r.id),
      code: String(r.code ?? "").toUpperCase(),
      discountType: (r.type ?? r.discountType) as "percentage" | "fixed",
      discountValue: Number(r.amount ?? r.discountValue ?? 0),

      // keep optionals nullable (not empty string)
      expirationDate: r.expirationDate ?? null,
      availableFrom: r.availableFrom ?? null,
      status: (r.status ?? "active") as "active" | "inactive",
    }));
  } catch (error) {
    console.error("Error fetching coupons:", error);
    return [];
  }
};



export const addCoupon = async (coupon: Coupon): Promise<boolean> => {
  try {
    await fetchJson(`${API_BASE}/coupons/create.php`, {
      method: "POST",
	  credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code: coupon.code,
        type: coupon.discountType,          // map fields if needed
        amount: coupon.discountValue,
        available_till: coupon.expirationDate ?? null, // maps to expirationDate
      }),
    });
    return true;
  } catch (error) {
    console.error("Error creating coupon:", error);
    return false;
  }
};

export const deleteCoupon = async (id: string): Promise<boolean> => {
  try {
    await fetchJson(`${API_BASE}/coupons/deactivate.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    });
    return true;
  } catch (error) {
    console.error("Error deactivating coupon:", error);
    return false;
  }
};

export const validateCoupon = async (code: string): Promise<Coupon | null> => {
  try {
    const data = await fetchJson(
      `${API_BASE}/coupons/validate.php?code=${encodeURIComponent(code)}`
    );

    // unwrap the PHP wrapper { success, coupon }
    return (data?.coupon ?? null) as Coupon | null;
  } catch (error) {
    console.error("Error validating coupon:", error);
    return null;
  }
};

/** ----------------------------
 * REVIEWS API (per chalet)
 * ---------------------------- */

export const getBestReviews = async (): Promise<BestReview[]> => {
  try {
    const data = await fetchJson(`${API_BASE}/apartments/getreviews.php`);
    const rows = unwrapRows(data) as BestReview[];
    return rows.slice(0, 6);
  } catch (error) {
    console.error("Error fetching best reviews:", error);
    return [];
  }
};

/** ----------------------------
 * EXTRAS API (per chalet)
 * ---------------------------- */


const normalizePriceType = (v: any): "one-time" | "per-day" | "per-person" => {
  if (v === "per-day") return "per-day";
  if (v === "per-person") return "per-person";
  return "one-time";
};

export const getExtras = async (chaletId: string): Promise<ChaletExtra[]> => {
  try {
    const data = await fetchJson(
      `${API_BASE}/extras/list.php?chalet_id=${encodeURIComponent(String(chaletId))}`
    );

    const rows = unwrapRows(data);

    return rows.map((r: any) => ({
      id: toString(r.id),
      chaletId: toString(r.chalet_id ?? r.chaletId ?? chaletId),
      label: toString(r.label),
      price: toNumber(r.price, 0),
      priceType: normalizePriceType(r.price_type ?? r.priceType),
    }));
  } catch (error) {
    console.error("Error fetching extras:", error);
    return [];
  }
};

export const addExtra = async (extra: ChaletExtra): Promise<{ success: boolean; id?: number }> => {
  try {
    const data = await fetchJson<{ success?: boolean; id?: number }>(`${API_BASE}/extras/create.php`, {
      method: "POST",
      body: JSON.stringify({
        chalet_id: extra.chaletId,
        label: extra.label,
		priceaf: undefined,
        price: extra.price,
        price_type: extra.priceType,
      }),
    });
    return { success: !!data?.success, id: data?.id };
  } catch (error) {
    console.error("Error adding extra:", error);
    return { success: false };
  }
};

export const updateExtra = async (extra: ChaletExtra): Promise<boolean> => {
  try {
    const data = await fetchJson<{ success?: boolean }>(`${API_BASE}/extras/update.php`, {
      method: "POST",
      body: JSON.stringify({
        id: extra.id,
        chalet_id: extra.chaletId,
        label: extra.label,
        price: extra.price,
        price_type: extra.priceType,
      }),
    });
    return !!data?.success;
  } catch (error) {
    console.error("Error updating extra:", error);
    return false;
  }
};

export const deleteExtra = async (id: string, chaletId: string): Promise<boolean> => {
  try {
    const data = await fetchJson<{ success?: boolean }>(`${API_BASE}/extras/delete.php`, {
      method: "POST",
      body: JSON.stringify({ id, chalet_id: chaletId }),
    });
    return !!data?.success;
  } catch (e) {
    console.error("Error deleting extra:", e);
    return false;
  }
};


/** ----------------------------
 * UTILITIES
 * ---------------------------- */

export const generateChaletDescription = async (
  title: string,
  keywords: string
): Promise<string> => {
  try {
    // ✅ Vite: use import.meta.env (not process.env)
    const apiKey =
      (import.meta as any).env?.VITE_GEMINI_API_KEY ||
      (import.meta as any).env?.GEMINI_API_KEY;

    if (!apiKey) return "No API Key configured.";

    const ai = new GoogleGenAI({ apiKey });

    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: `Write a premium Dutch rental description for "${title}". Keywords: ${keywords}. Approx 60 words.`,
    });

    return response.text || "";
  } catch (error) {
    console.error("Error generating description:", error);
    return "Fout bij genereren.";
  }
};


/** ----------------------------
 * Admin: Users management
 * ---------------------------- */

export type AdminUser = {
  id: number;
  username: string;
  email?: string | null;
  role: "admin" | "owner" | "cleaner";
  active: number; // 1/0
  created_at?: string;
  chaletIds: number[];
};

export const listUsers = async (): Promise<AdminUser[]> => {
  const j = await fetchJson<{ success?: boolean; users?: any[] }>(
    `${API_BASE}/admin/users/list.php`,
    { method: "GET" }
  );

  const users = Array.isArray((j as any)?.users) ? (j as any).users : Array.isArray(j as any) ? (j as any) : [];
  return users.map((u: any) => ({
    id: toNumber(u?.id),
    username: toString(u?.username),
    email: u?.email ?? null,
    role: (toString(u?.role) as any) || "owner",
    active: toNumber(u?.active, 1),
    created_at: toString(u?.created_at, ""),
    chaletIds: asArray<number>(u?.chaletIds).map((x) => toNumber(x)),
  }));
};

export const createUser = async (payload: {
  username: string;
  password: string;
  role: "admin" | "owner" | "cleaner";
  active: number; // 1/0
  email?: string | null;
}) => {
  return fetchJson(`${API_BASE}/admin/users/create.php`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const updateUser = async (payload: {
  id: number;
  role?: "admin" | "owner" | "cleaner";
  active?: number; // 1/0
  password?: string;
  email?: string | null;
}) => {
  return fetchJson(`${API_BASE}/admin/users/update.php`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const setUserChalets = async (userId: number, chaletIds: number[]) => {
  return fetchJson(`${API_BASE}/admin/users/set_chalets.php`, {
    method: "POST",
    body: JSON.stringify({ userId, chaletIds }),
  });
};

/** ----------------------------
 * Blog
 * ---------------------------- */

export const getBlogPosts = async (page = 1): Promise<BlogListResponse> => {
  const data = await fetchJson(`${API_BASE}/blog/list.php?page=${page}`);
  return data as BlogListResponse;
};

export const getAdminBlogPosts = async (page = 1): Promise<BlogListResponse> => {
  const data = await fetchJson(`${API_BASE}/blog/list.php?admin=1&page=${page}`);
  return data as BlogListResponse;
};

export const getBlogPost = async (idOrSlug: string): Promise<BlogPost | null> => {
  const param = /^\d+$/.test(idOrSlug) ? `id=${idOrSlug}` : `slug=${encodeURIComponent(idOrSlug)}`;
  const data = await fetchJson(`${API_BASE}/blog/get.php?${param}`);
  return data?.post ?? null;
};

export const createBlogPost = async (payload: Omit<BlogPost, 'id' | 'created_at' | 'updated_at'>): Promise<{ success: boolean; id?: number; slug?: string; error?: string }> => {
  return fetchJson(`${API_BASE}/blog/create.php`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const updateBlogPost = async (payload: Partial<BlogPost> & { id: string }): Promise<{ success: boolean; error?: string }> => {
  return fetchJson(`${API_BASE}/blog/update.php`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const deleteBlogPost = async (id: string): Promise<{ success: boolean; error?: string }> => {
  return fetchJson(`${API_BASE}/blog/delete.php`, {
    method: "POST",
    body: JSON.stringify({ id }),
  });
};
